import React, { useMemo, useState } from 'react'
import { AxiosError } from 'axios'
import { useQuery } from 'react-query'
import clsx from 'clsx'
import {
  AlertCircle,
  RefreshCw,
  BookOpen,
  CheckCircle2,
  TrendingUp,
  Target
} from 'lucide-react'
import { api } from '../../lib/api'
import { SkillGrid } from '../../components/SkillGrid'
import { useSkillmapGrid } from '../../hooks/useSkillmapGrid'
import {
  STATUS_BADGE_STYLES,
  STATUS_DESCRIPTIONS,
  STATUS_LABELS,
  STATUS_RING_COLORS
} from '../../lib/skillmap/constants'
import { darkenColor, lightenColor } from '../../lib/skillmap/colorUtils'
import { formatDateTime, formatNumber, formatPercent, formatScore, getCompetencyDisplayName } from '../../lib/skillmap/formatters'
import { CompetencyEntry, CompetencyStatus, CurriculumSummary, SkillmapData } from '../../lib/skillmap/types'

const fetchSkillmap = async (): Promise<SkillmapData> => {
  const response = await api.get('/student/skillmap')
  const { data } = response.data || {}

  if (!data) {
    throw new Error('Skillmap-Daten konnten nicht geladen werden.')
  }

  return data
}

const StudentSkillmap: React.FC = () => {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching
  } = useQuery<SkillmapData, AxiosError<{ message?: string }>>('student-skillmap', fetchSkillmap, {
    staleTime: 60 * 1000
  })
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [filterStatus, setFilterStatus] = useState<CompetencyStatus | 'all'>('all')
  const [collapsedCurricula, setCollapsedCurricula] = useState<Set<number>>(new Set())

  const { gridItems } = useSkillmapGrid({
    data,
    filterStatus,
    collapsedCurricula
  })

  const selectedCompetency = useMemo(() => {
    if (!data || !selectedId) return null
    const competencyId = selectedId.replace('competency-', '')
    return data.competencies.find((c) => c.id.toString() === competencyId) || null
  }, [data, selectedId])

  const curriculumById = useMemo(() => {
    if (!data) {
      return new Map<number, CurriculumSummary>()
    }
    return new Map(data.curricula.map((curriculum) => [curriculum.curriculumId, curriculum] as const))
  }, [data])


  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center space-y-4">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-600" />
          <p className="text-sm text-gray-500">Skillmap wird geladen ...</p>
        </div>
      </div>
    )
  }

  if (isError) {
    const axiosError = error as AxiosError<{ message?: string }>
    const message = axiosError?.response?.data?.message || error?.message || 'Die Skillmap konnte nicht geladen werden.'

    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-6 w-6 flex-shrink-0 text-red-500" />
          <div>
            <h2 className="text-lg font-semibold text-red-700">Fehler beim Laden der Skillmap</h2>
            <p className="mt-1 text-sm text-red-600">{message}</p>
            <button
              onClick={() => refetch()}
              className="mt-4 inline-flex items-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Erneut versuchen
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-600">
        Aktuell stehen keine Skillmap-Daten zur Verfügung.
      </div>
    )
  }

  const summary = data.summary

  const renderCompetencyDetail = (competency: CompetencyEntry) => {
    const curriculum = curriculumById.get(competency.curriculumId)
    const status = competency.status
    const badgeClass = STATUS_BADGE_STYLES[status]

    return (
      <div className="space-y-5">
        {competency.description && (
          <div className="rounded-lg border border-gray-100 bg-amber-50 p-4 text-sm text-amber-900">
            {competency.description}
          </div>
        )}

        <span
          className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${badgeClass}`}
        >
          {STATUS_LABELS[status]}
        </span>
        <p className="text-sm text-gray-600">{STATUS_DESCRIPTIONS[status]}</p>

        <div>
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Fortschritt</span>
            <span className="font-medium text-gray-900">
              {formatScore(competency.masteryScore, 0)}
            </span>
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-2 rounded-full"
              style={{
                width: `${Math.min(100, Math.max(0, competency.progress * 100))}%`,
                backgroundColor: STATUS_RING_COLORS[status]
              }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Level</p>
            <p className="mt-1 text-lg font-semibold text-gray-900">
              {competency.levelTitle || competency.levelAchieved || '–'}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Versuche</p>
            <p className="mt-1 text-lg font-semibold text-gray-900">{competency.attemptCount}</p>
          </div>
        </div>

        <div className="rounded-lg border border-gray-100 bg-white p-4 text-sm text-gray-600">
          <p>
            <span className="font-medium text-gray-900">Fach:</span>{' '}
            {curriculum?.label || curriculum?.subject || curriculum?.name || '–'}
          </p>
          <p>
            <span className="font-medium text-gray-900">Letzte Aktualisierung:</span>{' '}
            {formatDateTime(competency.lastUpdated)}
          </p>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-700">Bewertungen & Evidenzen</h3>
          {competency.courseBreakdown.length > 0 ? (
            <div className="mt-3 space-y-2 overflow-y-auto pr-1 text-sm text-gray-700">
              {competency.courseBreakdown.map((entry) => {
                const entryStatus = entry.status ?? 'not_attempted'
                const badge = STATUS_BADGE_STYLES[entryStatus as CompetencyStatus] || STATUS_BADGE_STYLES.not_attempted
                return (
                  <div
                    key={entry.userCompetencyId}
                    className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-gray-900">{entry.courseName || 'Allgemein'}</div>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badge}`}
                      >
                        {STATUS_LABELS[entryStatus as CompetencyStatus] || entryStatus}
                      </span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-600">
                      <div>
                        <span className="font-medium text-gray-700">Score:</span>{' '}
                        {entry.masteryScore !== null && entry.masteryScore !== undefined
                          ? formatScore(entry.masteryScore)
                          : '–'}
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Versuche:</span>{' '}
                        {entry.attemptCount}
                      </div>
                      {entry.updatedAt && (
                        <div className="col-span-2">
                          <span className="font-medium text-gray-700">Letzte Aktualisierung:</span>{' '}
                          {formatDateTime(entry.updatedAt)}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="mt-3 rounded-lg border border-dashed border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
              Für diese Kompetenz wurden noch keine Bewertungen erfasst.
            </p>
          )}
        </div>
      </div>
    )
  }


  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Skillmap</h1>
          <p className="mt-1 text-gray-600">
            Alle Kompetenzen auf einen Blick – farblich markiert siehst du sofort, was du bereits sicher beherrschst.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-60"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Aktualisieren
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
            <BookOpen className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Kompetenzen gesamt</p>
            <p className="text-xl font-semibold text-gray-900">{formatNumber(summary.totalCompetencies)}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
            <CheckCircle2 className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Beherrscht</p>
            <p className="text-xl font-semibold text-gray-900">{formatNumber(summary.mastered)}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-600">
            <Target className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">In Arbeit</p>
            <p className="text-xl font-semibold text-gray-900">{formatNumber(summary.inProgress)}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-sky-100 text-sky-600">
            <TrendingUp className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Ø Score</p>
            <p className="text-xl font-semibold text-gray-900">{formatScore(summary.averageScore)}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Filter:</span>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as CompetencyStatus | 'all')}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="all">Alle anzeigen</option>
            <option value="in_progress">In Arbeit</option>
            <option value="competent">Kompetent</option>
            <option value="proficient">Proficient</option>
            <option value="not_yet_competent">Noch nicht sicher</option>
            <option value="not_attempted">Nicht gestartet</option>
          </select>
              </div>
        {data && data.curricula.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Fächer:</span>
            <div className="flex flex-wrap gap-2">
              {data.curricula.map((curriculum) => {
                const isCollapsed = collapsedCurricula.has(curriculum.curriculumId)
                return (
                  <button
                    key={curriculum.curriculumId}
                    onClick={() => {
                      const newSet = new Set(collapsedCurricula)
                      if (isCollapsed) {
                        newSet.delete(curriculum.curriculumId)
                      } else {
                        newSet.add(curriculum.curriculumId)
                      }
                      setCollapsedCurricula(newSet)
                    }}
                    className={clsx(
                      'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition',
                      isCollapsed
                        ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                    )}
                  >
                        <span
                      className="inline-block h-2 w-2 rounded-full"
                      style={{ backgroundColor: curriculum.color }}
                    />
                    {curriculum.label || curriculum.subject || curriculum.name || 'Curriculum'}
                    {isCollapsed ? ' (ausgeblendet)' : ''}
                  </button>
                )
              })}
                      </div>
                  </div>
                )}
                            </div>

      {/* Legende für Hierarchieebenen */}
      {gridItems.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">Hierarchieebenen</h3>
          <p className="mb-3 text-xs text-gray-600">
            Die Farben zeigen die hierarchische Struktur: Ebene 1 nutzt Grundfarben, Ebene 2 dunklere Varianten, Ebene 3 blassere Varianten.
          </p>
          <div className="flex flex-wrap gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded border border-gray-300" style={{ backgroundColor: '#eff6ff', borderWidth: '2px', borderColor: '#3b82f6' }} />
              <span className="text-gray-600">Ebene 0/1: Grundfarben (Hauptkompetenzen)</span>
                              </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded border border-gray-300" style={{ backgroundColor: darkenColor('#eff6ff', 0.15), borderWidth: '2px', borderColor: darkenColor('#3b82f6', 0.15) }} />
              <span className="text-gray-600">Ebene 2: Dunklere Varianten</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded border border-gray-300" style={{ backgroundColor: lightenColor(darkenColor('#eff6ff', 0.15), 0.3), borderWidth: '2px', borderColor: lightenColor(darkenColor('#3b82f6', 0.15), 0.2) }} />
              <span className="text-gray-600">Ebene 3+: Blassere Varianten</span>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="grid grid-cols-1 lg:grid-cols-3">
          <div className="col-span-2 bg-gray-50 p-6">
            {gridItems.length === 0 ? (
              <div className="flex h-full items-center justify-center p-12 text-center text-sm text-gray-600">
                Es liegen noch keine Kompetenzdaten vor. Sobald Bewertungen vorhanden sind, erscheint hier die Skillmap.
                              </div>
            ) : (
              <SkillGrid 
                items={gridItems}
                className="p-2"
                onTileClick={(item) => setSelectedId(item.id)}
              />
            )}
          </div>

          <div className="border-t border-gray-200 bg-white p-4 lg:border-l lg:border-t-0 lg:p-6">
            <div className="space-y-4">
              {selectedCompetency ? (
                <>
              <div className="flex items-start justify-between gap-3">
                <div>
                      <h2 className="text-xl font-semibold text-gray-900">{getCompetencyDisplayName(selectedCompetency)}</h2>
                      {curriculumById.get(selectedCompetency.curriculumId) && (
                        <p className="text-sm text-gray-500">
                          {curriculumById.get(selectedCompetency.curriculumId)?.label || 
                           curriculumById.get(selectedCompetency.curriculumId)?.subject || 
                           curriculumById.get(selectedCompetency.curriculumId)?.name}
                        </p>
                  )}
                </div>
                  <button
                      onClick={() => setSelectedId(null)}
                    className="text-sm text-primary-600 hover:text-primary-500"
                  >
                      Schließen
                  </button>
              </div>
                  {renderCompetencyDetail(selectedCompetency)}
                </>
              ) : (
                <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                  Klicke auf eine Kompetenz-Kachel, um Details zu sehen.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StudentSkillmap
